import { RAW_PAX_CREDITS_PER_DISPLAY_CREDIT } from "@pax/api-contract/accounting/v1alpha1";
import { EVERYONE_ROLE_ID, GamePermission, GameRole } from "@pax/api-contract/games/v1alpha1";
import type { LobbyCopy } from "@pax/api-contract/internal/v1alpha1";
import { CustomPerspectiveSchema, IdSchema } from "@pax/world";
import { z } from "zod";
import { IdempotencyKey, ModerationRefusal, Revision } from "./schemas";

export { EVERYONE_ROLE_ID, GamePermission, GameRole };

/** Every permission, in the order a role editor lists them. */
export const PERMISSIONS: readonly GamePermission[] = GamePermission.options;

/** The admin role every lobby starts with. Admins rank below the owner and manage everyone else. */
export const ADMIN_ROLE_ID = "admin";

const PLAYING: GamePermission[] = ["chat", "forkGame", "voteToJump", "intervene", "useAdvisor"];

/** The roles every lobby starts with: the one everyone holds, and admins. */
export const STARTING_ROLES: readonly GameRole[] = [
  { id: EVERYONE_ROLE_ID, name: "Player", rank: 0, permissions: PLAYING },
  {
    id: ADMIN_ROLE_ID,
    name: "Admin",
    rank: 100,
    permissions: [
      ...PLAYING,
      "renameGame",
      "manageRoles",
      "deleteMessages",
      "pinMessages",
      "changeGameMaster",
      "viewRules",
      "editRules",
      "manageJoining",
      "makeSpectators",
      "removePeople",
      "runJumps",
      "pauseGame",
    ],
  },
];

const ChatTextSchema = z.string().trim().min(1).max(4_000);

const LobbyPollOptionSchema = z.strictObject({
  id: IdSchema,
  label: z.string().trim().min(1).max(200),
});

/** The shortest and longest a jump timer runs: a minute, and thirty days. */
export const JUMP_TIMER_RANGE_MS = { min: 60_000, max: 30 * 24 * 60 * 60_000 } as const;

/** Where someone asks to play: a suggestion, a perspective of their own, or alongside others. */
export const PlayDestinationSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("suggestion"), suggestionId: IdSchema }),
  CustomPerspectiveSchema,
  z.strictObject({ type: z.literal("perspective"), handle: IdSchema }),
]);
export type PlayDestination = z.infer<typeof PlayDestinationSchema>;

/** Raw credits, as the ledger counts them, written as a decimal string. */
const RawCreditsSchema = z.string().regex(/^[1-9]\d{0,30}$/);

/** How the game master's calls are paid for: everyone playing splits them, or some people cover everyone. */
export const PaymentModeSchema = z.enum(["split", "cover"]);
export type PaymentMode = z.infer<typeof PaymentModeSchema>;

/**
 * When someone playing can't pay their share, with everyone splitting. `othersCover`: the rest
 * take on their share. `spectator`: they stop playing. `pause`: the game pauses until someone
 * decides.
 */
export const CantPayRuleSchema = z.enum(["othersCover", "spectator", "pause"]);
export type CantPayRule = z.infer<typeof CantPayRuleSchema>;

/** When someone covering runs out. `othersCover`: the others covering take their share. `pause`: the game pauses. */
export const CoverRunsOutRuleSchema = z.enum(["othersCover", "pause"]);
export type CoverRunsOutRule = z.infer<typeof CoverRunsOutRuleSchema>;

/**
 * What a perspective does while everyone playing it is offline. `ai`: an AI plays it. `idle`: it
 * does nothing. `spectator`: its players become spectators.
 */
export const OfflinePlanSchema = z.enum(["ai", "idle", "spectator"]);
export type OfflinePlan = z.infer<typeof OfflinePlanSchema>;

/**
 * The host's rule for offline players: one plan for every perspective, or the plans each
 * perspective's players may choose between.
 */
export const OfflineRuleSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("forced"), plan: OfflinePlanSchema }),
  z.strictObject({
    type: z.literal("playersChoose"),
    allowed: z
      .array(OfflinePlanSchema)
      .min(2)
      .refine(plans => new Set(plans).size === plans.length, "Each plan is listed once."),
  }),
]);
export type OfflineRule = z.infer<typeof OfflineRuleSchema>;

/** What a lobby starts with: players choose any plan. */
const STARTING_OFFLINE_RULE: OfflineRule = {
  type: "playersChoose",
  allowed: ["ai", "idle", "spectator"],
};

/** The plan a perspective whose players haven't chosen falls back to, cheapest first. */
const FALLBACK_PLANS: readonly OfflinePlan[] = ["idle", "spectator", "ai"];

/** The most a member spends on an AI playing for them, until they choose: five credits. */
export const DEFAULT_OFFLINE_LIMIT_RAW = (5n * RAW_PAX_CREDITS_PER_DISPLAY_CREDIT).toString();

export const PlayAnswerSchema = z.enum(["yes", "no"]);
export type PlayAnswer = z.infer<typeof PlayAnswerSchema>;

/**
 * Why an ask to play ended without reaching the game master. `coopClosed`: the perspective was
 * closed to co-op. `emptied`: nobody plays it anymore. `left`: the asker left the game. `full`: the
 * game reached its player limit. `gone`: what they asked for no longer exists.
 */
export const PlayAskDropReasonSchema = z.enum(["coopClosed", "emptied", "left", "full", "gone"]);
export type PlayAskDropReason = z.infer<typeof PlayAskDropReasonSchema>;

export const LobbyOperationSchema = z.discriminatedUnion("type", [
  /** The lobby's first delta, which the session commits from what the API holds about the game. */
  z.strictObject({
    type: z.literal("lobby.started"),
    ownerId: z.uuid(),
    roles: z.array(GameRole),
  }),
  z.strictObject({ type: z.literal("chat.posted"), messageId: IdSchema, text: ChatTextSchema }),
  z.strictObject({
    type: z.literal("poll.created"),
    pollId: IdSchema,
    question: z.string().trim().min(1).max(1_000),
    options: z
      .array(LobbyPollOptionSchema)
      .min(2)
      .max(10)
      .refine(
        options => new Set(options.map(option => option.id)).size === options.length,
        "Poll choices need different ids.",
      ),
  }),
  /** A vote replaces the member's earlier one, and a null choice takes it back. */
  z.strictObject({
    type: z.literal("poll.voted"),
    pollId: IdSchema,
    optionId: IdSchema.nullable(),
  }),
  z.strictObject({ type: z.literal("poll.closed"), pollId: IdSchema }),
  /** Takes a message or poll down. The session then stores `chat.redacted` in its place. */
  z.strictObject({ type: z.literal("chat.deleted"), id: IdSchema }),
  /** Stands where a deleted message or poll was, so what it said is never sent again. */
  z.strictObject({ type: z.literal("chat.redacted"), id: IdSchema }),
  /** Pins a message or poll across the top of the chat for everyone, replacing the one pinned. */
  z.strictObject({ type: z.literal("chat.pinned"), id: IdSchema }),
  z.strictObject({ type: z.literal("chat.unpinned") }),
  z.strictObject({ type: z.literal("role.created"), role: GameRole }),
  z.strictObject({
    type: z.literal("role.updated"),
    roleId: IdSchema,
    changes: GameRole.omit({ id: true }).partial(),
  }),
  /** Deleting a role takes it from everyone who held it. */
  z.strictObject({ type: z.literal("role.deleted"), roleId: IdSchema }),
  /** Every role the member holds beyond the everyone role, replacing the ones they held. */
  z.strictObject({
    type: z.literal("member.rolesSet"),
    userId: z.uuid(),
    roleIds: z.array(IdSchema),
  }),
  /** Takes a member off the perspective they play. The session commits the change to the world. */
  z.strictObject({ type: z.literal("member.madeSpectator"), userId: z.uuid() }),
  /** The game's new owner. The previous owner becomes an admin while the admin role exists. */
  z.strictObject({ type: z.literal("ownership.transferred"), userId: z.uuid() }),
  /**
   * Stops the game master and the jump timer until someone resumes. Chat, actions, and asks to play
   * carry on, and the game master answers them once it runs again.
   */
  z.strictObject({ type: z.literal("game.paused") }),
  z.strictObject({ type: z.literal("game.resumed") }),
  /** How long a round lasts before the game jumps, ready or not, or null for no timer. */
  z.strictObject({
    type: z.literal("jumpTimer.set"),
    afterMs: z.number().int().min(JUMP_TIMER_RANGE_MS.min).max(JUMP_TIMER_RANGE_MS.max).nullable(),
  }),
  /** Starts the jump now, without waiting for everyone to be ready. */
  z.strictObject({ type: z.literal("jump.forced") }),
  /** Whether a member who manages joining approves each ask to play before the game master sees it. */
  z.strictObject({ type: z.literal("joining.approvalSet"), required: z.boolean() }),
  /** How many people can play perspectives at once, or null for no limit. The rest spectate. */
  z.strictObject({
    type: z.literal("joining.playerLimitSet"),
    limit: z.number().int().min(1).max(64).nullable(),
  }),
  z.strictObject({ type: z.literal("payment.modeSet"), mode: PaymentModeSchema }),
  z.strictObject({ type: z.literal("payment.cantPaySet"), rule: CantPayRuleSchema }),
  z.strictObject({ type: z.literal("payment.coverRunsOutSet"), rule: CoverRunsOutRuleSchema }),
  /** Asks a member to help cover everyone. They pay nothing until they agree. */
  z.strictObject({ type: z.literal("cover.asked"), userId: z.uuid() }),
  /** A member offers to help cover, up to their limit, for someone who changes who pays to accept. */
  z.strictObject({ type: z.literal("cover.offered"), limitRaw: RawCreditsSchema.nullable() }),
  /** The member asked agrees to cover, up to their limit in raw credits, or with no limit. */
  z.strictObject({ type: z.literal("cover.agreed"), limitRaw: RawCreditsSchema.nullable() }),
  z.strictObject({ type: z.literal("cover.declined") }),
  /** Someone covering stops, or whoever changes who pays stops them; an ask or offer ends too. */
  z.strictObject({ type: z.literal("cover.stopped"), userId: z.uuid() }),
  z.strictObject({ type: z.literal("cover.limitSet"), limitRaw: RawCreditsSchema.nullable() }),
  /**
   * Members whose share of a game master call couldn't be paid, as the game records it, and
   * whether that paused the game.
   */
  z.strictObject({
    type: z.literal("payment.ranShort"),
    userIds: z.array(z.uuid()).min(1),
    paused: z.boolean(),
  }),
  /**
   * A member who ran short says they added credits. They pay again from the next call, or while the
   * game waits on a decision, trying again shows whether they did.
   */
  z.strictObject({ type: z.literal("payment.toppedUp") }),
  /**
   * What happens after people ran short: carry on without them, which takes them off their
   * perspectives or out of covering, or try again with them. Either resumes the game.
   */
  z.strictObject({ type: z.literal("payment.resolved"), choice: z.enum(["without", "retry"]) }),
  /**
   * What happens to perspectives while everyone playing them is offline, and how long someone stays
   * offline before becoming a spectator when that's their plan.
   */
  z.strictObject({
    type: z.literal("offline.ruleSet"),
    rule: OfflineRuleSchema,
    spectateAfterMs: z
      .number()
      .int()
      .min(60_000)
      .max(7 * 24 * 60 * 60_000),
  }),
  /** A perspective's plan for when everyone playing it is offline, and what the AI plays from. */
  z.strictObject({
    type: z.literal("offline.planSet"),
    handle: IdSchema,
    plan: OfflinePlanSchema,
    notes: z.string().trim().max(2_000),
  }),
  /** The most the member spends on an AI playing for them, in raw credits, or null for no limit. */
  z.strictObject({ type: z.literal("offline.limitSet"), limitRaw: RawCreditsSchema.nullable() }),
  /** Whether anyone may ask to join a perspective someone else plays. */
  z.strictObject({ type: z.literal("coop.set"), allowed: z.boolean() }),
  /** Whether anyone may ask to join this perspective, as whoever plays it decides. */
  z.strictObject({ type: z.literal("coop.perspectiveSet"), handle: IdSchema, open: z.boolean() }),
  /**
   * An ask to play. Asking to join a perspective others play needs each of them to say yes, and
   * while the game approves asks, a member who manages joining approves it too. Then it goes on to
   * the game master.
   */
  z.strictObject({ type: z.literal("play.asked"), askId: IdSchema, to: PlayDestinationSchema }),
  /** One answer from someone playing the perspective asked for. A single no ends the ask. */
  z.strictObject({ type: z.literal("play.answered"), askId: IdSchema, answer: PlayAnswerSchema }),
  z.strictObject({ type: z.literal("play.approved"), askId: IdSchema }),
  z.strictObject({ type: z.literal("play.refused"), askId: IdSchema }),
  z.strictObject({ type: z.literal("play.withdrawn"), askId: IdSchema }),
  /** Everyone who had to say yes did, so the ask goes on to the game master as the asker's own. */
  z.strictObject({ type: z.literal("play.forwarded"), askId: IdSchema }),
  z.strictObject({
    type: z.literal("play.dropped"),
    askId: IdSchema,
    reason: PlayAskDropReasonSchema,
  }),
]);
export type LobbyOperation = z.infer<typeof LobbyOperationSchema>;
export type LobbyOperationType = LobbyOperation["type"];

export const LobbyActorSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("user"), userId: z.uuid() }),
  z.strictObject({ type: z.literal("system") }),
]);
export type LobbyActor = z.infer<typeof LobbyActorSchema>;

/** One change to a game's lobby. Every member reads every lobby delta. */
export const LobbyDeltaSchema = z.strictObject({
  revision: z.number().int().positive(),
  committedAt: z.iso.datetime({ offset: true }),
  actor: LobbyActorSchema,
  operation: LobbyOperationSchema,
});
export type LobbyDelta = z.infer<typeof LobbyDeltaSchema>;

/** What a member sends. The session fills in who sent it and when. */
export const LobbyProposalSchema = z.strictObject({
  idempotencyKey: IdempotencyKey,
  operations: z.array(LobbyOperationSchema).min(1).max(20),
});
export type LobbyProposal = z.infer<typeof LobbyProposalSchema>;

export const LobbyIssueCodeSchema = z.enum([
  "not_permitted",
  "outranked",
  "unknown_reference",
  "already_exists",
  "invalid_change",
  "rate_limited",
]);
export type LobbyIssueCode = z.infer<typeof LobbyIssueCodeSchema>;

export const LobbyIssueSchema = z.strictObject({
  code: LobbyIssueCodeSchema,
  path: z.array(z.union([z.string(), z.number()])),
  message: z.string(),
});
export type LobbyIssue = z.infer<typeof LobbyIssueSchema>;

export const LobbyProposalResult = z.discriminatedUnion("status", [
  z.strictObject({
    status: z.literal("committed"),
    idempotencyKey: IdempotencyKey,
    throughRevision: Revision.meta({
      description: "The proposal is applied once the socket's lobby head reaches this revision.",
    }),
  }),
  z.strictObject({
    status: z.literal("rejected"),
    idempotencyKey: IdempotencyKey,
    issues: z.array(LobbyIssueSchema).min(1),
    currentRevision: Revision.meta({ description: "The lobby revision it was checked against." }),
  }),
  z.strictObject({
    status: z.literal("refused"),
    idempotencyKey: IdempotencyKey,
    moderation: ModerationRefusal,
  }),
]);
export type LobbyProposalResult = z.infer<typeof LobbyProposalResult>;

export interface LobbyMessage {
  type: "message";
  id: string;
  authorId: string;
  text: string;
  revision: number;
  committedAt: string;
  deleted: boolean;
}

export interface LobbyPoll {
  type: "poll";
  id: string;
  authorId: string;
  question: string;
  options: { id: string; label: string }[];
  /** Each member's choice, by user id. */
  votes: Map<string, string>;
  closed: boolean;
  revision: number;
  committedAt: string;
  deleted: boolean;
}

export type LobbyChatItem = LobbyMessage | LobbyPoll;

/** Someone covering everyone, or asked or offering to. */
export interface Coverer {
  status: "asked" | "offered" | "covering";
  /** The most they spend in this game, in raw credits. Null for no limit. */
  limitRaw: string | null;
  /** Who asked them, while they're asked. */
  askedBy: string | null;
  /** The lobby revision their status last changed at. */
  revision: number;
}

/** Someone whose share of a game master call couldn't be paid. */
export interface Shortfall {
  /** When the game found out. */
  since: string;
  /** Whether they say they added credits since. */
  toppedUp: boolean;
}

/** An ask to play, and where it stands. */
export interface PlayAsk {
  id: string;
  askerId: string;
  to: PlayDestination;
  /** Each answer so far from the people playing the perspective asked for, by who gave it. */
  answers: Map<string, PlayAnswer>;
  /** `waiting` until a member who manages joining approves it. Null when nobody had to. */
  approval: "waiting" | "approved" | null;
  status: "open" | "declined" | "refused" | "withdrawn" | "forwarded" | "dropped";
  droppedBecause: PlayAskDropReason | null;
  revision: number;
}

/** A lobby's current state, reduced from its deltas in revision order. */
export interface LobbyState {
  revision: number;
  /** The revision of the latest delta that changed what the API keeps a copy of. */
  copyRevision: number;
  ownerId: string | null;
  roles: Map<string, GameRole>;
  /** The roles each member holds beyond the everyone role. */
  memberRoles: Map<string, string[]>;
  /** Messages and polls in the order they were sent. A deleted one stays, emptied, in its place. */
  chat: LobbyChatItem[];
  chatById: Map<string, LobbyChatItem>;
  /** The message or poll pinned across the top of the chat, and who pinned it. */
  pin: { id: string; pinnedBy: string } | null;
  /**
   * Who paused the game, and when, while it's paused. `by` is null when the game paused itself
   * because people ran short.
   */
  paused: { by: string | null; at: string } | null;
  /** How long a round lasts before the game jumps, ready or not. Null for no timer. */
  jumpTimerMs: number | null;
  joining: {
    /** Whether a member who manages joining approves each ask to play. */
    approval: boolean;
    /** How many people can play perspectives at once. Null for no limit. */
    playerLimit: number | null;
  };
  payment: { mode: PaymentMode; cantPay: CantPayRule; coverRunsOut: CoverRunsOutRule };
  offline: {
    rule: OfflineRule;
    spectateAfterMs: number;
    /** Each perspective's plan, as its players last chose it, by handle. */
    plans: Map<string, { plan: OfflinePlan; notes: string; setBy: string; at: string }>;
    /** The limit each member chose, in raw credits, or null for none. Absent is the default. */
    limits: Map<string, string | null>;
  };
  coverers: Map<string, Coverer>;
  /** Who ran short of credits for the game master's calls, until someone decides or they try again. */
  shortfall: Map<string, Shortfall>;
  coop: {
    allowed: boolean;
    /** The perspectives whose players closed them to co-op, by handle. */
    closed: Set<string>;
  };
  playAsks: Map<string, PlayAsk>;
}

export function emptyLobby(): LobbyState {
  return {
    revision: 0,
    copyRevision: 0,
    ownerId: null,
    roles: new Map(),
    memberRoles: new Map(),
    chat: [],
    chatById: new Map(),
    pin: null,
    paused: null,
    jumpTimerMs: null,
    joining: { approval: false, playerLimit: null },
    payment: { mode: "split", cantPay: "spectator", coverRunsOut: "othersCover" },
    offline: {
      rule: STARTING_OFFLINE_RULE,
      spectateAfterMs: 10 * 60_000,
      plans: new Map(),
      limits: new Map(),
    },
    coverers: new Map(),
    shortfall: new Map(),
    coop: { allowed: true, closed: new Set() },
    playAsks: new Map(),
  };
}

/**
 * What a perspective does while everyone playing it is offline: the plan the host forces, or its
 * players' choice among those allowed. Without a choice it does nothing, if the rule allows that.
 */
export function offlinePlanOf(
  lobby: LobbyState,
  handle: string,
): { plan: OfflinePlan; notes: string } {
  const chosen = lobby.offline.plans.get(handle);
  const notes = chosen?.notes ?? "";
  const allowed = offlinePlansAllowed(lobby.offline.rule);
  if (chosen && allowed.includes(chosen.plan)) return { plan: chosen.plan, notes };
  const plan = FALLBACK_PLANS.find(fallback => allowed.includes(fallback)) ?? "idle";
  return { plan, notes };
}

/** The plans the host's rule leaves a perspective's players: the one it forces, or those it allows. */
export function offlinePlansAllowed(rule: OfflineRule): readonly OfflinePlan[] {
  return rule.type === "forced" ? [rule.plan] : rule.allowed;
}

/** The most a member spends on an AI playing for them, in raw credits, or null for no limit. */
export function offlineLimitOf(lobby: LobbyState, userId: string): string | null {
  return lobby.offline.limits.has(userId)
    ? (lobby.offline.limits.get(userId) ?? null)
    : DEFAULT_OFFLINE_LIMIT_RAW;
}

/** The members covering everyone now, in the order they started. */
export function coveringIds(lobby: LobbyState): string[] {
  return [...lobby.coverers]
    .filter(([, coverer]) => coverer.status === "covering")
    .sort(([, left], [, right]) => left.revision - right.revision)
    .map(([userId]) => userId);
}

/** The ask to play a member has open, if any. A member asks for one thing at a time. */
export function openPlayAskOf(lobby: LobbyState, userId: string): PlayAsk | null {
  for (const ask of lobby.playAsks.values()) {
    if (ask.askerId === userId && ask.status === "open") return ask;
  }
  return null;
}

/** Whether people may ask to join this perspective now. */
export function isOpenToCoop(lobby: LobbyState, handle: string): boolean {
  return lobby.coop.allowed && !lobby.coop.closed.has(handle);
}

/** Whether an open ask still waits on a member who manages joining. */
export function awaitsApproval(lobby: LobbyState, ask: PlayAsk): boolean {
  return ask.approval === "waiting" && lobby.joining.approval;
}

/** Applies one committed delta. Deltas are applied once each, in revision order. */
export function applyLobbyDelta(lobby: LobbyState, delta: LobbyDelta): void {
  lobby.revision = delta.revision;
  if (changesLobbyCopy(delta.operation.type)) lobby.copyRevision = delta.revision;
  const { operation, revision, committedAt } = delta;
  const actorId = delta.actor.type === "user" ? delta.actor.userId : "";
  switch (operation.type) {
    case "lobby.started":
      lobby.ownerId = operation.ownerId;
      lobby.roles = new Map(operation.roles.map(role => [role.id, role]));
      return;
    case "chat.posted":
      addChatItem(lobby, {
        type: "message",
        id: operation.messageId,
        authorId: actorId,
        text: operation.text,
        revision,
        committedAt,
        deleted: false,
      });
      return;
    case "poll.created":
      addChatItem(lobby, {
        type: "poll",
        id: operation.pollId,
        authorId: actorId,
        question: operation.question,
        options: operation.options,
        votes: new Map(),
        closed: false,
        revision,
        committedAt,
        deleted: false,
      });
      return;
    case "chat.redacted":
      addChatItem(lobby, {
        type: "message",
        id: operation.id,
        authorId: actorId,
        text: "",
        revision,
        committedAt,
        deleted: true,
      });
      return;
    case "poll.voted": {
      const poll = lobby.chatById.get(operation.pollId);
      if (poll?.type !== "poll") return;
      if (operation.optionId === null) poll.votes.delete(actorId);
      else poll.votes.set(actorId, operation.optionId);
      return;
    }
    case "poll.closed": {
      const poll = lobby.chatById.get(operation.pollId);
      if (poll?.type === "poll") poll.closed = true;
      return;
    }
    case "chat.deleted": {
      const item = lobby.chatById.get(operation.id);
      if (!item) return;
      item.deleted = true;
      if (item.type === "message") {
        item.text = "";
      } else {
        item.question = "";
        item.options = [];
        item.votes.clear();
      }
      if (lobby.pin?.id === operation.id) lobby.pin = null;
      return;
    }
    case "chat.pinned":
      lobby.pin = { id: operation.id, pinnedBy: actorId };
      return;
    case "chat.unpinned":
      lobby.pin = null;
      return;
    case "role.created":
      lobby.roles.set(operation.role.id, operation.role);
      return;
    case "role.updated": {
      const role = lobby.roles.get(operation.roleId);
      if (role) lobby.roles.set(role.id, { ...role, ...operation.changes });
      return;
    }
    case "role.deleted":
      lobby.roles.delete(operation.roleId);
      for (const [userId, roleIds] of lobby.memberRoles) {
        setMemberRoles(
          lobby,
          userId,
          roleIds.filter(roleId => roleId !== operation.roleId),
        );
      }
      return;
    case "member.rolesSet":
      setMemberRoles(lobby, operation.userId, operation.roleIds);
      return;
    case "member.madeSpectator":
      return;
    case "ownership.transferred": {
      const previous = lobby.ownerId;
      lobby.ownerId = operation.userId;
      if (previous && lobby.roles.has(ADMIN_ROLE_ID)) {
        const held = lobby.memberRoles.get(previous) ?? [];
        if (!held.includes(ADMIN_ROLE_ID))
          setMemberRoles(lobby, previous, [...held, ADMIN_ROLE_ID]);
      }
      return;
    }
    case "game.paused":
      lobby.paused = { by: actorId, at: committedAt };
      return;
    case "payment.modeSet":
      lobby.payment.mode = operation.mode;
      lobby.shortfall.clear();
      if (lobby.paused?.by === null) lobby.paused = null;
      return;
    case "payment.cantPaySet":
      lobby.payment.cantPay = operation.rule;
      return;
    case "payment.coverRunsOutSet":
      lobby.payment.coverRunsOut = operation.rule;
      return;
    case "cover.asked": {
      const offered = lobby.coverers.get(operation.userId);
      lobby.coverers.set(
        operation.userId,
        offered?.status === "offered"
          ? { status: "covering", limitRaw: offered.limitRaw, askedBy: null, revision }
          : { status: "asked", limitRaw: null, askedBy: actorId, revision },
      );
      return;
    }
    case "cover.offered":
      lobby.coverers.set(actorId, {
        status: "offered",
        limitRaw: operation.limitRaw,
        askedBy: null,
        revision,
      });
      return;
    case "cover.agreed":
      lobby.coverers.set(actorId, {
        status: "covering",
        limitRaw: operation.limitRaw,
        askedBy: null,
        revision,
      });
      return;
    case "cover.declined":
      lobby.coverers.delete(actorId);
      return;
    case "cover.stopped":
      lobby.coverers.delete(operation.userId);
      lobby.shortfall.delete(operation.userId);
      return;
    case "cover.limitSet": {
      const coverer = lobby.coverers.get(actorId);
      if (coverer) coverer.limitRaw = operation.limitRaw;
      lobby.shortfall.delete(actorId);
      return;
    }
    case "payment.ranShort":
      for (const userId of operation.userIds) {
        lobby.shortfall.set(userId, { since: committedAt, toppedUp: false });
      }
      if (operation.paused) lobby.paused = { by: null, at: committedAt };
      return;
    case "payment.toppedUp": {
      const short = lobby.shortfall.get(actorId);
      if (!short) return;
      if (lobby.paused?.by === null) short.toppedUp = true;
      else lobby.shortfall.delete(actorId);
      return;
    }
    case "payment.resolved":
      lobby.shortfall.clear();
      if (lobby.paused?.by === null) lobby.paused = null;
      return;
    case "game.resumed":
      lobby.paused = null;
      return;
    case "jumpTimer.set":
      lobby.jumpTimerMs = operation.afterMs;
      return;
    case "jump.forced":
      return;
    case "joining.approvalSet":
      lobby.joining.approval = operation.required;
      return;
    case "joining.playerLimitSet":
      lobby.joining.playerLimit = operation.limit;
      return;
    case "offline.ruleSet":
      lobby.offline.rule = operation.rule;
      lobby.offline.spectateAfterMs = operation.spectateAfterMs;
      return;
    case "offline.planSet":
      lobby.offline.plans.set(operation.handle, {
        plan: operation.plan,
        notes: operation.notes,
        setBy: actorId,
        at: committedAt,
      });
      return;
    case "offline.limitSet":
      lobby.offline.limits.set(actorId, operation.limitRaw);
      return;
    case "coop.set":
      lobby.coop.allowed = operation.allowed;
      return;
    case "coop.perspectiveSet":
      if (operation.open) lobby.coop.closed.delete(operation.handle);
      else lobby.coop.closed.add(operation.handle);
      return;
    case "play.asked":
      lobby.playAsks.set(operation.askId, {
        id: operation.askId,
        askerId: actorId,
        to: operation.to,
        answers: new Map(),
        approval:
          lobby.joining.approval && !permissionsOf(lobby, actorId).has("manageJoining")
            ? "waiting"
            : null,
        status: "open",
        droppedBecause: null,
        revision,
      });
      return;
    case "play.answered": {
      const ask = lobby.playAsks.get(operation.askId);
      if (!ask) return;
      ask.answers.set(actorId, operation.answer);
      if (operation.answer === "no") ask.status = "declined";
      return;
    }
    case "play.approved": {
      const ask = lobby.playAsks.get(operation.askId);
      if (ask) ask.approval = "approved";
      return;
    }
    case "play.refused":
    case "play.withdrawn":
    case "play.forwarded": {
      const ask = lobby.playAsks.get(operation.askId);
      if (!ask) return;
      if (operation.type === "play.forwarded" && lobby.paused?.by !== null) {
        lobby.shortfall.delete(ask.askerId);
      }
      ask.status =
        operation.type === "play.refused"
          ? "refused"
          : operation.type === "play.withdrawn"
            ? "withdrawn"
            : "forwarded";
      return;
    }
    case "play.dropped": {
      const ask = lobby.playAsks.get(operation.askId);
      if (!ask) return;
      ask.status = "dropped";
      ask.droppedBecause = operation.reason;
      return;
    }
  }
}

function addChatItem(lobby: LobbyState, item: LobbyChatItem): void {
  lobby.chat.push(item);
  lobby.chatById.set(item.id, item);
}

function setMemberRoles(lobby: LobbyState, userId: string, roleIds: readonly string[]): void {
  if (roleIds.length === 0) lobby.memberRoles.delete(userId);
  else lobby.memberRoles.set(userId, [...roleIds]);
}

/**
 * What a member may do: everything for the owner, otherwise the everyone role and their own roles
 * together. Editing the rules lets them read the rules too.
 */
export function permissionsOf(lobby: LobbyState, userId: string): ReadonlySet<GamePermission> {
  if (userId === lobby.ownerId) return new Set(PERMISSIONS);
  const granted = new Set<GamePermission>(lobby.roles.get(EVERYONE_ROLE_ID)?.permissions ?? []);
  for (const roleId of lobby.memberRoles.get(userId) ?? []) {
    for (const permission of lobby.roles.get(roleId)?.permissions ?? []) granted.add(permission);
  }
  if (granted.has("editRules")) granted.add("viewRules");
  return granted;
}

/** A member's standing: above everyone for the owner, otherwise the highest rank among their roles. */
export function rankOf(lobby: LobbyState, userId: string): number {
  if (userId === lobby.ownerId) return Number.POSITIVE_INFINITY;
  let rank = lobby.roles.get(EVERYONE_ROLE_ID)?.rank ?? 0;
  for (const roleId of lobby.memberRoles.get(userId) ?? []) {
    rank = Math.max(rank, lobby.roles.get(roleId)?.rank ?? 0);
  }
  return rank;
}

/** Whether a delta of this type changes what the API keeps a copy of. */
export function changesLobbyCopy(type: LobbyOperationType): boolean {
  return (
    type === "lobby.started" ||
    type.startsWith("role.") ||
    type === "member.rolesSet" ||
    type === "ownership.transferred"
  );
}

/** The lobby's current state as the API keeps it, once the lobby has started. */
export function lobbyCopy(lobby: LobbyState): LobbyCopy | null {
  if (!lobby.ownerId) return null;
  const asUserId = (userId: string) => userId as LobbyCopy["ownerId"];
  return {
    revision: lobby.copyRevision,
    ownerId: asUserId(lobby.ownerId),
    roles: [...lobby.roles.values()],
    memberRoles: [...lobby.memberRoles].map(([userId, roleIds]) => ({
      userId: asUserId(userId),
      roleIds,
    })),
  };
}

/** What judging a lobby change needs from outside the lobby: who is in the game, and who plays what. */
export interface LobbySurroundings {
  isMember(userId: string): boolean;
  /** Everyone playing the perspective, or null when there is no such perspective. */
  playersOf(handle: string): readonly string[] | null;
  /** Whether the user plays a perspective now. */
  isPlaying(userId: string): boolean;
  /** How many people play perspectives now. */
  playerCount(): number;
  /** Whether the spectator view still offers the suggestion. */
  isOffered(suggestionId: string): boolean;
  /** Whether the user is waiting on the game master to change perspective. */
  isAskingToPlay(userId: string): boolean;
  /** Whether a jump is running. */
  jumpIsActive(): boolean;
}

/** Why a member may not make a change now, or null when they may. */
export function lobbyIssue(
  lobby: LobbyState,
  userId: string,
  operation: LobbyOperation,
  surroundings: LobbySurroundings,
): LobbyIssue | null {
  const { isMember } = surroundings;
  const permissions = permissionsOf(lobby, userId);
  const rank = rankOf(lobby, userId);
  const isOwner = userId === lobby.ownerId;
  const refuse = (code: LobbyIssueCode, message: string): LobbyIssue => ({
    code,
    path: [],
    message,
  });
  const need = (permission: GamePermission, doing: string): LobbyIssue | null =>
    permissions.has(permission)
      ? null
      : refuse("not_permitted", `Your roles don't let you ${doing}.`);
  const grantable = (granted: readonly GamePermission[]): LobbyIssue | null =>
    granted.every(permission => permissions.has(permission))
      ? null
      : refuse("not_permitted", "A role can't give permissions you don't have yourself.");
  const below = (roleRank: number): LobbyIssue | null =>
    roleRank < rank ? null : refuse("outranked", "That role ranks at or above your own.");
  const openAsk = (askId: string): PlayAsk | LobbyIssue => {
    const ask = lobby.playAsks.get(askId);
    if (!ask) return refuse("unknown_reference", "There's no such ask.");
    return ask.status === "open" ? ask : refuse("invalid_change", "That ask is over.");
  };

  switch (operation.type) {
    case "lobby.started":
    case "chat.redacted":
    case "play.forwarded":
    case "play.dropped":
    case "payment.ranShort":
      return refuse("not_permitted", "Only the game records this.");
    case "chat.posted":
      return (
        need("chat", "chat in the lobby") ??
        (lobby.chatById.has(operation.messageId)
          ? refuse("already_exists", "That message was already sent.")
          : null)
      );
    case "poll.created":
      return (
        need("chat", "post polls") ??
        (lobby.chatById.has(operation.pollId)
          ? refuse("already_exists", "That poll was already posted.")
          : null)
      );
    case "poll.voted": {
      const poll = lobby.chatById.get(operation.pollId);
      if (poll?.type !== "poll" || poll.deleted) {
        return refuse("unknown_reference", "There's no such poll.");
      }
      if (poll.closed) return refuse("invalid_change", "That poll is closed.");
      if (
        operation.optionId !== null &&
        !poll.options.some(option => option.id === operation.optionId)
      ) {
        return refuse("unknown_reference", "That poll has no such choice.");
      }
      return need("chat", "vote in polls");
    }
    case "poll.closed": {
      const poll = lobby.chatById.get(operation.pollId);
      if (poll?.type !== "poll" || poll.deleted) {
        return refuse("unknown_reference", "There's no such poll.");
      }
      if (poll.closed) return refuse("invalid_change", "That poll is already closed.");
      return poll.authorId === userId ? null : need("deleteMessages", "close other people's polls");
    }
    case "chat.deleted": {
      const item = lobby.chatById.get(operation.id);
      if (!item || item.deleted) return refuse("unknown_reference", "There's no such message.");
      return item.authorId === userId
        ? null
        : need("deleteMessages", "delete other people's messages");
    }
    case "chat.pinned": {
      const item = lobby.chatById.get(operation.id);
      if (!item || item.deleted) return refuse("unknown_reference", "There's no such message.");
      if (lobby.pin?.id === operation.id) return refuse("invalid_change", "It's already pinned.");
      return need("pinMessages", "pin messages");
    }
    case "chat.unpinned":
      if (!lobby.pin) return refuse("invalid_change", "Nothing is pinned.");
      return need("pinMessages", "unpin messages");
    case "role.created": {
      if (operation.role.id === EVERYONE_ROLE_ID || lobby.roles.has(operation.role.id)) {
        return refuse("already_exists", "A role with that id exists.");
      }
      return (
        need("manageRoles", "manage roles") ??
        below(operation.role.rank) ??
        grantable(operation.role.permissions)
      );
    }
    case "role.updated": {
      const role = lobby.roles.get(operation.roleId);
      if (!role) return refuse("unknown_reference", "There's no such role.");
      const { changes } = operation;
      if (role.id === EVERYONE_ROLE_ID && changes.rank !== undefined && changes.rank !== 0) {
        return refuse("invalid_change", "The everyone role always ranks 0.");
      }
      const added = (changes.permissions ?? []).filter(
        permission => !role.permissions.includes(permission),
      );
      return (
        need("manageRoles", "manage roles") ??
        below(role.rank) ??
        (changes.rank === undefined ? null : below(changes.rank)) ??
        grantable(added)
      );
    }
    case "role.deleted": {
      const role = lobby.roles.get(operation.roleId);
      if (!role) return refuse("unknown_reference", "There's no such role.");
      if (role.id === EVERYONE_ROLE_ID) {
        return refuse("invalid_change", "The everyone role can't be deleted.");
      }
      return need("manageRoles", "manage roles") ?? below(role.rank);
    }
    case "member.rolesSet": {
      const target = operation.userId;
      if (!isMember(target)) return refuse("unknown_reference", "They aren't in this game.");
      if (target === lobby.ownerId) return refuse("outranked", "Nobody manages the owner.");
      if (target === userId && !isOwner) {
        return refuse("invalid_change", "You can't change your own roles.");
      }
      const unknown = operation.roleIds.find(
        roleId => roleId === EVERYONE_ROLE_ID || !lobby.roles.has(roleId),
      );
      if (unknown !== undefined)
        return refuse("unknown_reference", `There's no role "${unknown}".`);
      const held = lobby.memberRoles.get(target) ?? [];
      const changed = [
        ...operation.roleIds.filter(roleId => !held.includes(roleId)),
        ...held.filter(roleId => !operation.roleIds.includes(roleId)),
      ];
      return (
        need("manageRoles", "manage roles") ??
        (rankOf(lobby, target) < rank ? null : refuse("outranked", "They rank at or above you.")) ??
        changed.map(roleId => below(lobby.roles.get(roleId)?.rank ?? 0)).find(Boolean) ??
        null
      );
    }
    case "member.madeSpectator": {
      const target = operation.userId;
      if (!isMember(target)) return refuse("unknown_reference", "They aren't in this game.");
      if (!surroundings.isPlaying(target)) {
        return refuse("invalid_change", "They're already spectating.");
      }
      return (
        need("makeSpectators", "make people spectators") ??
        (rankOf(lobby, target) < rank ? null : refuse("outranked", "They rank at or above you."))
      );
    }
    case "ownership.transferred": {
      if (!isOwner) return refuse("not_permitted", "Only the owner can hand the game on.");
      if (!isMember(operation.userId)) {
        return refuse("unknown_reference", "They aren't in this game.");
      }
      if (operation.userId === userId) return refuse("invalid_change", "You already own it.");
      return null;
    }
    case "game.paused":
      if (lobby.paused) return refuse("invalid_change", "The game is already paused.");
      return need("pauseGame", "pause the game");
    case "game.resumed":
      if (!lobby.paused) return refuse("invalid_change", "The game isn't paused.");
      if (lobby.paused.by === null) {
        return refuse("invalid_change", "Someone ran short. Decide what happens to resume.");
      }
      return need("pauseGame", "resume the game");
    case "payment.modeSet":
    case "payment.cantPaySet":
    case "payment.coverRunsOutSet":
      return need("changeWhoPays", "change who pays");
    case "cover.asked": {
      if (!isMember(operation.userId))
        return refuse("unknown_reference", "They aren't in this game.");
      const coverer = lobby.coverers.get(operation.userId);
      if (coverer && coverer.status !== "offered") {
        return refuse("already_exists", "They're already asked or covering.");
      }
      return need("changeWhoPays", "choose who covers");
    }
    case "cover.offered":
      return lobby.coverers.has(userId)
        ? refuse("already_exists", "You're already asked, offering, or covering.")
        : null;
    case "cover.agreed":
    case "cover.declined":
      return lobby.coverers.get(userId)?.status === "asked"
        ? null
        : refuse("invalid_change", "Nobody asked you to cover.");
    case "cover.stopped":
      if (!lobby.coverers.has(operation.userId)) {
        return refuse("unknown_reference", "They aren't asked, offering, or covering.");
      }
      return operation.userId === userId ? null : need("changeWhoPays", "choose who covers");
    case "cover.limitSet":
      return lobby.coverers.get(userId)?.status === "covering"
        ? null
        : refuse("invalid_change", "You aren't covering.");
    case "payment.toppedUp":
      return lobby.shortfall.has(userId) ? null : refuse("invalid_change", "You didn't run short.");
    case "payment.resolved":
      if (lobby.shortfall.size === 0) return refuse("invalid_change", "Nobody ran short.");
      return operation.choice === "without"
        ? need("makeSpectators", "carry on without them")
        : need("runJumps", "try again");
    case "jumpTimer.set":
      return need("runJumps", "change the jump timer");
    case "jump.forced":
      if (lobby.paused) return refuse("invalid_change", "The game is paused.");
      if (surroundings.jumpIsActive()) return refuse("invalid_change", "A jump is running.");
      return need("runJumps", "start a jump without waiting");
    case "joining.approvalSet":
    case "joining.playerLimitSet":
    case "coop.set":
      return need("manageJoining", "change who can play");
    case "coop.perspectiveSet":
      return surroundings.playersOf(operation.handle)?.includes(userId)
        ? null
        : refuse("not_permitted", "Only someone playing it can open or close it to co-op.");
    case "offline.ruleSet":
      return need("changeWhoPays", "change what happens while people are offline");
    case "offline.planSet":
      if (!surroundings.playersOf(operation.handle)?.includes(userId)) {
        return refuse("not_permitted", "Only someone playing it can choose its plan.");
      }
      return offlinePlansAllowed(lobby.offline.rule).includes(operation.plan)
        ? null
        : refuse("invalid_change", "The host doesn't allow that plan.");
    case "offline.limitSet":
      return null;
    case "play.asked": {
      if (lobby.playAsks.has(operation.askId)) {
        return refuse("already_exists", "That ask was already made.");
      }
      const { to } = operation;
      if (to.type === "perspective") {
        const players = surroundings.playersOf(to.handle);
        if (!players) return refuse("unknown_reference", "There's no such perspective.");
        if (players.length === 0) return refuse("invalid_change", "Nobody plays it to join.");
        if (players.includes(userId)) return refuse("invalid_change", "You already play it.");
        if (!lobby.coop.allowed) return refuse("invalid_change", "This game doesn't allow co-op.");
        if (lobby.coop.closed.has(to.handle)) {
          return refuse("invalid_change", "Its players closed it to co-op.");
        }
      }
      if (to.type === "suggestion" && !surroundings.isOffered(to.suggestionId)) {
        return refuse("unknown_reference", "That suggestion is gone.");
      }
      if (openPlayAskOf(lobby, userId) || surroundings.isAskingToPlay(userId)) {
        return refuse("invalid_change", "You're already asking to play.");
      }
      const { playerLimit } = lobby.joining;
      if (
        playerLimit !== null &&
        !surroundings.isPlaying(userId) &&
        surroundings.playerCount() >= playerLimit
      ) {
        return refuse("invalid_change", "The game is full.");
      }
      return null;
    }
    case "play.answered": {
      const ask = openAsk(operation.askId);
      if (!("askerId" in ask)) return ask;
      if (
        ask.to.type !== "perspective" ||
        !surroundings.playersOf(ask.to.handle)?.includes(userId)
      ) {
        return refuse("not_permitted", "Only someone playing it can answer.");
      }
      return ask.answers.has(userId) ? refuse("already_exists", "You already answered.") : null;
    }
    case "play.approved":
    case "play.refused": {
      const ask = openAsk(operation.askId);
      if (!("askerId" in ask)) return ask;
      if (!awaitsApproval(lobby, ask)) {
        return refuse("invalid_change", "That ask isn't waiting for approval.");
      }
      return need("manageJoining", "approve asks to play");
    }
    case "play.withdrawn": {
      const ask = openAsk(operation.askId);
      if (!("askerId" in ask)) return ask;
      return ask.askerId === userId
        ? null
        : refuse("not_permitted", "Only the asker can take it back.");
    }
  }
}
